import * as Constants from '@/redux/constants';
import * as Actions from '@/redux/actions';
import { Stack, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import paymentIcons from 'assets/payment-icon.png';
import qfpayIcon from 'assets/qfpay-icon.png';
import { GeneralInput } from 'components/input';
import { PaymentModal } from 'components/payment-modal';
import { useDispatch, useForm, useLocale } from 'hooks';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { useEffect, useState, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import FundSelectBox from '../../entry/components/FundSelectBox';
import GiveButton from '../../entry/components/GiveButton';
import Subheading from '../../entry/components/Subheading';
import TypeSelectBox from '../../entry/components/TypeSelectBox';
import PortalHeader from '../components/PortalHeader';

function PortalGivePage() {
  const dispatch = useDispatch();
  const t = useI18n();
  const locale = useLocale();
  const {
    itemInfo,
    optionsItemInfo,
    control,
    errors,
    watch,
    setValue,
    fields,
    registerOptions,
    isFetching,
    onSubmit,
  } = useForm({
    model: 'Giving',
    formType: 'public',
    payload: {
      locale,
    },
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    switch (itemInfo.type) {
      case Constants.CREATE_GIVING.SUCCESS: {
        const paymentUrl = get(itemInfo, 'data.onlinePayment.paymentUrl');
        const paymentIntentId = get(itemInfo, 'data.onlinePayment.paymentIntentId');

        if (paymentUrl) {
          location.href = paymentUrl;
        } else if (paymentIntentId) {
          setShowPaymentModal(true);
        }
      }
        break;
    }
  }, [itemInfo]);

  const watchedFundId = watch('fundId');
  const watchedType = watch('type');
  const watchedAmount = watch('amount');

  // Auto-select payment method if there's only one option
  useEffect(() => {
    const paymentMethods = get(optionsItemInfo, 'data.paymentMethodId', []);
    if (paymentMethods.length === 1 && !watch('paymentMethodId')) {
      setValue('paymentMethodId', paymentMethods[0].value);
    }
  }, [optionsItemInfo, setValue, watch]);

  const fieldMap = fields.reduce((acc, field) => {
    field = field[0]; // Unwrap field array

    if (field.name === 'fundId' || field.name === 'type') {
      return acc;
    }

    acc[field.name] = (
      <GeneralInput
        key={`portal-give-input-${field.name}`}
        {...field}
        control={control}
        registerOptions={registerOptions[field.name]}
        options={get(optionsItemInfo, `data.${field.name}`)}
        readOnly={isFetching}
        error={get(errors, field.name)}
        helper={get(errors, `${field.name}.message`) ?? field.helper}
      />
    );
    return acc;
  }, {});

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  const onClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    dispatch(Actions.clearCreateGiving());
  }, [dispatch]);

  return (
    <Stack sx={{
      backgroundColor: 'background.default',
    }}>
      <PortalHeader header={t('GIVING')} />

      <form onSubmit={onSubmit} className="w-full flex flex-col items-center">
        <Grid container gap={3} direction="row" sx={{ p: 3, alignItems: 'center' }}>
          <Controller
            name="fundId"
            control={control}
            rules={registerOptions.fundId}
            render={() => {
              const error = get(errors, 'fundId');
              return (
                <FormControl
                  variant='filled'
                  error={!!error}
                  fullWidth
                >
                  <Grid container gap={2} direction="row" sx={{ alignItems: 'center', justifyContent: 'center', pt: 3, pb: 1 }}>
                    {
                      optionsItemInfo.data?.fundId?.map((fund, index) => (
                        <FundSelectBox
                          key={`fund-select-box-${index}`}
                          fund={fund}
                          selected={fund.value === watchedFundId}
                          onSelect={() => setValue('fundId', watchedFundId === fund.value ? null : fund.value)}
                        />
                      ))
                    }
                  </Grid>
                  <FormHelperText sx={{ textAlign: 'center' }}>{t(get(error, 'message'))}</FormHelperText>
                </FormControl>
              );
            }}
          />
        </Grid>

        <Grid container direction="column" sx={{ alignItems: 'center', py: 3, px: 4, width: { xs: '100%', sm: '500px' } }}>
          <Subheading>{t('FREQUENCY')}</Subheading>

          <Controller
            name="type"
            control={control}
            rules={registerOptions.type}
            render={() => {
              const error = get(errors, 'type');
              return (
                <FormControl
                  variant='filled'
                  error={!!error}
                  fullWidth
                >
                  <Stack gap={2} sx={{ flexDirection: isMobile ? 'column' : 'row', display: 'flex', justifyContent: 'center', maxWidth: '100%', pb: 1 }}>
                    {
                      optionsItemInfo.data?.type?.map((type, index) => (
                        <TypeSelectBox
                          key={`type-select-box-${index}`}
                          type={type}
                          selected={type.value === watchedType}
                          onSelect={() => setValue('type', watchedType === type.value ? null : type.value)}
                        />
                      ))
                    }
                  </Stack>
                  <FormHelperText>{get(error, 'message')}</FormHelperText>
                </FormControl>
              );
            }}
          />
        </Grid>

        <Grid sx={{
          width: '100%',
          maxWidth: '500px',
          p: 4,
        }}>
          <Subheading>{t('AMOUNT')}</Subheading>

          <Stack gap={1}>
            {fieldMap.amount}
            {fieldMap.paymentMethodId}
          </Stack>

          <Stack gap={1} sx={{ mb: 4 }}>
            <GiveButton
              amount={watchedAmount}
              isFetching={isFetching}
            />
          </Stack>

          <Stack gap={1.5} sx={{ flexDirection: 'column', display: 'flex', alignItems: 'center', py: 1.5 }}>
            <Typography variant='caption' align='center' color='info.700'>
              {t('Click this button to proceed to the QFPay payment page')}
            </Typography>

            <PaymentModal
              open={showPaymentModal}
              onClose={onClosePaymentModal}
              data={itemInfo.data}
            />

            <img src={qfpayIcon} alt='qfpayIcon' />

            <img src={paymentIcons} alt='paymentIcons' />
          </Stack>

        </Grid>
      </form>
    </Stack>
  );
}

export default PortalGivePage;
